/** XeAgents.ai**/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  ANALYSIS_STEPS,
  emptyContent,
  type AIContentState,
  type ContentOrigin,
  type DetectedProduct,
  type ExtractionStage,
  type FlowTemplate,
  type SalesContentField,
  type TemplateVariable,
  emptyIcp,
  INDUSTRIES,
  OTHER_INDUSTRY,
  hasIcp,
  icpForRequest,
  audienceFor,
  type IcpProfile,
} from "./types";
import {
  applyGeneratedContent,
  applyGeneratedValues,
  audienceRefreshPlan,
  collectErrors,
  planHasWork,
  settleGenerating,
  settleGeneratingValues,
  withoutAudienceOwnedValues,
  SALES_FIELDS,
} from "./rules";

/**
 * Everything the customize page knows, in one place.
 *
 * The page below it is layout. Keeping the rules here - what is required, what
 * counts as user-edited, what a re-extract is allowed to overwrite - means they
 * are stated once and can be read without wading through JSX, and it is what
 * stops "never overwrite the user's words" from being re-implemented slightly
 * differently in three sections.
 */

/**
 * Four fields the page asks for by name that are ALSO template variables.
 *
 * Company Name and Product/Service are on the form as themselves, not as
 * "{{company_name}}", because that is how a person thinks about them - but they
 * are the same values the script substitutes, so they live in one map and are
 * rendered once. Without this the page would show Company Name in Step 2 and
 * "company name" again in Step 3's variable list.
 */
export const BOUND_VARIABLE_KEYS = ['company_name', 'product_name', 'target_audience', 'value_proposition'] as const;

/** Enough material to be worth asking a model to read. */
const MIN_SOURCE_CHARS = 40;

/**
 * The page's own fields, written out as text a model can read.
 *
 * The fallback when there is no website and no document: somebody who typed
 * their company name and product into Step 2 has still told us enough to draft
 * a value proposition from, and refusing to try would make Generate look broken.
 */
function describeManually(current: Record<string, string>): string {
  return Object.entries(current)
    .filter(([, entry]) => String(entry || '').trim())
    .map(([key, entry]) => `${key.replace(/_/g, ' ')}: ${entry}`)
    .join('\n');
}

/** The answers a template-made flow was saved with (see FlowTemplateProvenance on the server). */
export interface SavedCustomization {
  name: string;
  language?: string;
  flowVariables?: Record<string, string>;
  customInstructions?: string;
  salesContent?: { valueProposition?: string; keyFeatures?: string; objections?: string };
  icp?: Record<string, string>;
  /** What was read from the website or document when the flow was made. */
  sourceDocument?: string;
}

export interface CustomizerOptions {
  template: FlowTemplate | null;
  isCustomTemplate: boolean;
  /** Variables the custom (non-template) path collects, which declares its own set. */
  customVariables: TemplateVariable[];
}

export function useTemplateCustomizer({ template, isCustomTemplate, customVariables }: CustomizerOptions) {
  const { toast } = useToast();

  // ---------------------------------------------------------------- step 1
  const [flowName, setFlowName] = useState("");
  const [language, setLanguage] = useState("en");

  // ---------------------------------------------------------------- step 2
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  // Provenance for the template's own placeholders, so each one can show
  // "AI Generated" / "Edited by you" and be protected from a regeneration the
  // same way the three sales fields are.
  const [variableOrigins, setVariableOrigins] = useState<Record<string, ContentOrigin>>({});
  const [sourceText, setSourceText] = useState("");
  const [icp, setIcp] = useState<IcpProfile>(emptyIcp());
  // The profile as it was when the current copy was written, so a later change
  // can be pointed out without rewriting anything on its own.
  const [icpAtLastWrite, setIcpAtLastWrite] = useState<string>("");
  const [extraction, setExtraction] = useState<ExtractionStage>({ status: 'idle' });

  // ---------------------------------------------------------------- step 3/4
  const [salesContent, setSalesContent] = useState<Record<SalesContentField, AIContentState>>({
    valueProposition: emptyContent(),
    keyFeatures: emptyContent(),
    objections: emptyContent(),
  });
  const [instructions, setInstructions] = useState<AIContentState>(emptyContent());

  // Errors appear once a field has been visited or a save attempted, so an
  // untouched form is not a wall of red on arrival.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busyFields, setBusyFields] = useState<SalesContentField[]>([]);

  const declaredVariables = useMemo<TemplateVariable[]>(
    () => (isCustomTemplate ? customVariables : template?.requiredVariables ?? [])
      .filter(variable => !variable.fromAgent && !variable.fromContact),
    [isCustomTemplate, customVariables, template],
  );

  /** The template's own fields, minus the four the form already asks for by name. */
  const extraVariables = useMemo(
    () => declaredVariables.filter(variable => !BOUND_VARIABLE_KEYS.includes(variable.key as any)),
    [declaredVariables],
  );

  const declares = useCallback(
    (key: string) => declaredVariables.some(variable => variable.key === key),
    [declaredVariables],
  );

  // ------------------------------------------------------------- validation

  /**
   * Mark a field as visited, so its error may now be shown.
   *
   * Separate from setting a value because leaving a field empty is exactly the
   * case worth reporting, and that produces no change to record.
   */
  const touch = useCallback((key: string) => {
    setTouched(previous => (previous[key] ? previous : { ...previous, [key]: true }));
  }, []);

  const setVariable = useCallback((key: string, value: string) => {
    setVariables(previous => ({ ...previous, [key]: value }));
    setTouched(previous => ({ ...previous, [key]: true }));
    // Typing makes it theirs, exactly as it does for the sales fields: from here
    // a regeneration has to ask first.
    setVariableOrigins(previous => ({ ...previous, [key]: 'edited' }));
  }, []);

  const setSalesField = useCallback((field: SalesContentField, value: string) => {
    // Typing makes it theirs. From here a regeneration has to ask first, and a
    // re-extract leaves it alone entirely.
    setSalesContent(previous => ({ ...previous, [field]: { value, origin: 'edited' } }));
    setTouched(previous => ({ ...previous, [field]: true }));
  }, []);

  /**
   * What is in the box, character for character.
   *
   * Inputs must be bound to this and never to `value` below: a trimmed accessor
   * feeding a controlled input eats the space the moment it is typed, so
   * "Acme Corp" cannot be entered at all.
   */
  const raw = useCallback((key: string) => String(variables[key] ?? ""), [variables]);

  /** The same field with the whitespace taken off, for deciding whether it is filled. */
  const value = useCallback((key: string) => raw(key).trim(), [raw]);

  const errors = useMemo(
    () => collectErrors({
      flowName, language, variableValue: value, salesContent, extraVariables,
      icp: icpForRequest(icp),
    }),
    [flowName, language, value, salesContent, extraVariables, icp],
  );

  const showError = useCallback(
    (key: string) => (submitAttempted || touched[key] ? errors[key] : undefined),
    [errors, submitAttempted, touched],
  );

  /**
   * The audience answers report themselves once anything on the page has been.
   *
   * A dropdown has no blur worth waiting for, so the usual touched gate left
   * these showing a red asterisk and no message. It used to open up only when a
   * website address was entered, because these were optional until then; they
   * are required now, so the gate is simply "has this page been started" -
   * which keeps a freshly opened form from greeting someone with three errors
   * while still naming what is missing the moment they engage with it.
   *
   * The button's hint names these fields outright, so nothing depends on this
   * alone to explain a disabled Generate.
   */
  const pageStarted = websiteUrl.trim().length > 0 || Object.keys(touched).length > 0;
  const showAudienceError = useCallback(
    (key: string) => (submitAttempted || pageStarted ? errors[key] : undefined),
    [errors, submitAttempted, pageStarted],
  );

  const isValid = Object.keys(errors).length === 0;

  // -------------------------------------------------------------- generation

  const applyGenerated = useCallback(
    (generated: Partial<Record<SalesContentField, string>>, force: SalesContentField[] = []) => {
      setSalesContent(previous => applyGeneratedContent(previous, generated, force));
    },
    [],
  );

  /**
   * Write copy, template values, or both.
   *
   * One request rather than two: the placeholders are fragments of the same
   * pitch ("we're a {{service_name}} firm brought in to {{outcome_delivered}}"),
   * so writing them in the same breath as the value proposition is what keeps
   * them saying the same thing. Sending them separately produced a script that
   * argued with itself.
   *
   * @param variableKeys the template's own placeholders to fill, if any
   */
  const generateSales = useCallback(
    async (
      fields: SalesContentField[],
      content: string,
      force = false,
      instruction = '',
      variableKeys: string[] = [],
    ) => {
      if (content.trim().length < MIN_SOURCE_CHARS) {
        toast({
          title: "Nothing to write from yet",
          description: "Read your website, or add your company details, first.",
          variant: "destructive",
        });
        return;
      }

      const specs = declaredVariables.filter(v => variableKeys.includes(v.key));

      setBusyFields(fields);
      setSalesContent(previous => {
        const next = { ...previous };
        for (const field of fields) next[field] = { ...previous[field], origin: 'generating' };
        return next;
      });
      setVariableOrigins(previous => {
        const next = { ...previous };
        for (const key of variableKeys) next[key] = 'generating';
        return next;
      });

      try {
        const response = await apiRequest("POST", "/api/flow-automation/generate-sales-content", {
          content,
          companyName: value('company_name'),
          productName: value('product_name'),
          targetAudience: value('target_audience'),
          templateName: template?.name,
          fields,
          templateVariables: specs.map(v => ({
            key: v.key,
            description: v.description,
            example: v.example,
          })),
          icp: icpForRequest(icp),
          instruction: instruction || undefined,
          currentDraft: fields.length === 1
            ? salesContent[fields[0]].value.trim() || undefined
            : variableKeys.length === 1 ? raw(variableKeys[0]).trim() || undefined : undefined,
        });
        const data = await response.json();
        applyGenerated(data.content || {}, force ? fields : []);

        if (variableKeys.length > 0) {
          const incoming = (data.values || data.content?.values || {}) as Record<string, string>;
          // Judged against the origins as they were BEFORE this run marked them
          // generating, so "never overwrite what the user typed" is decided by
          // what they typed, not by the spinner we just put there.
          const applied = applyGeneratedValues(
            variables, variableOrigins, incoming, force ? variableKeys : [],
          );
          setVariables(applied.values);
          setVariableOrigins(settleGeneratingValues(applied.values, applied.origins, variableKeys));
        }

        // A field the model could not write must not be left spinning.
        setSalesContent(previous => settleGenerating(previous, fields));
        // The copy now reflects this profile; a later change to it is what makes
        // the "audience changed" note appear.
        setIcpAtLastWrite(JSON.stringify(icpForRequest(icp)));
      } catch (error: any) {
        setSalesContent(previous => settleGenerating(previous, fields));
        setVariableOrigins(previous => settleGeneratingValues(variables, previous, variableKeys));
        toast({
          title: "Could not write that section",
          description: `${error.message} — write it in yourself to carry on.`,
          variant: "destructive",
        });
      } finally {
        setBusyFields([]);
      }
    },
    [applyGenerated, declaredVariables, icp, raw, salesContent, template?.name, toast, value, variables, variableOrigins],
  );

  /**
   * Rewrite one field, doing what the user asked for in the dialog.
   *
   * `force` is true because the dialog IS the consent - it showed the current
   * text, named the overwrite, and the user pressed Rewrite. Only this field is
   * forced; an edited neighbour is not part of that consent.
   */
  const rewrite = useCallback(
    (field: SalesContentField, instruction: string) =>
      generateSales([field], sourceText || describeManually(variables), true, instruction),
    [generateSales, sourceText, variables],
  );

  /**
   * The same, for one of the template's own placeholders.
   *
   * No sales fields are requested, so a rewrite of "proof point" cannot quietly
   * rewrite the value proposition underneath it.
   */
  const rewriteVariable = useCallback(
    (key: string, instruction: string) =>
      generateSales([], sourceText || describeManually(variables), true, instruction, [key]),
    [generateSales, sourceText, variables],
  );

  const suggestInstructions = useCallback(
    async (content?: string, force = false) => {
      const material = (content ?? sourceText).trim();
      if (material.length < 80) return;
      if (instructions.origin === 'edited' && !force) return;

      setInstructions(previous => ({ ...previous, origin: 'generating' }));
      try {
        const response = await apiRequest("POST", "/api/flow-automation/suggest-custom-instructions", {
          content: material,
          templateName: template?.name,
          templateDescription: template?.description,
          icp: icpForRequest(icp),
        });
        const data = await response.json();
        if (typeof data.text === 'string' && data.text.trim()) {
          setInstructions({ value: data.text.trim(), origin: 'ai' });
          return;
        }
        setInstructions(previous => ({ value: previous.value, origin: previous.value ? 'edited' : 'empty' }));
      } catch {
        // Instructions are optional; an empty box is a perfectly good outcome
        // and must not interrupt the rest of the page.
        setInstructions(previous => ({ value: previous.value, origin: previous.value ? 'edited' : 'empty' }));
      }
    },
    [icp, instructions.origin, sourceText, template?.description, template?.name],
  );

  // -------------------------------------------------------------- extraction

  const pullVariables = useCallback(
    async (content: string) => {
      const response = await apiRequest("POST", "/api/flow-automation/extract-company-variables", {
        content,
        requiredVariables: declaredVariables,
      });
      const data = await response.json();
      const found: Record<string, string> = {};
      for (const [key, entry] of Object.entries(data.variables ?? {})) {
        const text = typeof entry === 'string' ? entry : String(entry ?? '');
        if (text.trim()) found[key] = text.trim();
      }

      // Same rule as the sales copy: what the user typed outranks what was found.
      setVariables(previous => {
        const next = { ...previous };
        for (const [key, entry] of Object.entries(withoutAudienceOwnedValues(found, !!icp.industry))) {
          if (touched[key] && String(previous[key] ?? '').trim()) continue;
          next[key] = entry;
        }
        return next;
      });

      return found;
    },
    [declaredVariables, touched, icp.industry],
  );

  /**
   * Read the website, then fill the page from it.
   *
   * The visible checklist tracks the real calls rather than a timer: each step
   * ticks when the work behind it has actually finished, so a slow site shows a
   * slow checklist instead of a complete one over an empty form.
   */
  const extractFromWebsite = useCallback(
    async (productUrl?: string, productName?: string) => {
      const url = websiteUrl.trim();
      if (!url) {
        toast({ title: "Add your website address first", variant: "destructive" });
        return;
      }

      setExtraction({ status: 'analyzing', step: 0 });
      try {
        const response = await apiRequest("POST", "/api/flow-automation/extract-from-website", {
          url,
          productUrl,
          templateId: template?.id,
        });
        const data = await response.json();

        if (data.status === 'needs_product_selection') {
          // Several products on one homepage cannot become one pitch. Asking is
          // the point - guessing produces a script describing a company that
          // sells everything.
          setExtraction({
            status: 'choosing_product',
            products: (data.products || []) as DetectedProduct[],
            siteName: data.siteName || '',
          });
          return;
        }

        const text = String(data.text || '').trim();
        setSourceText(text);
        setExtraction({ status: 'analyzing', step: 1 });

        await pullVariables(text);
        setExtraction({ status: 'analyzing', step: 2 });

        await generateSales(SALES_FIELDS, text, false, '', extraVariables.map(v => v.key));
        setExtraction({ status: 'analyzing', step: 4 });

        await suggestInstructions(text);

        setExtraction({
          status: 'done',
          scope: data.scope === 'product' ? 'product' : 'company',
          pagesRead: Array.isArray(data.pagesRead) ? data.pagesRead : [],
          productName,
        });
        toast({
          title: "Website analyzed successfully",
          description: "Review the details below — you can edit anything before saving.",
        });
      } catch (error: any) {
        setExtraction({ status: 'failed', message: error.message || "We could not read that site." });
        toast({
          title: "Could not read that website",
          description: `${error.message} — you can still fill the fields in by hand.`,
          variant: "destructive",
        });
      }
    },
    [extraVariables, generateSales, pullVariables, suggestInstructions, template?.id, toast, websiteUrl],
  );

  const chooseProduct = useCallback(
    (product: DetectedProduct) => extractFromWebsite(product.url, product.name),
    [extractFromWebsite],
  );

  const resetExtraction = useCallback(() => setExtraction({ status: 'idle' }), []);

  // ---------------------------------------------------------------- audience

  /**
   * Record an audience answer, and keep the spoken audience line in step.
   *
   * {{target_audience}} is substituted into the greeting and the pitch, so it
   * cannot be left to a separate box someone might not fill - that box was the
   * Target Audience field, asking the same question as Industry and putting a
   * hole in a spoken line whenever the two disagreed. Industry is the answer
   * now; this writes the sentence it implies.
   */
  const setIcpField = useCallback(
    (patch: Partial<IcpProfile>) =>
      setIcp(previous => {
        const next = { ...previous, ...patch };
        if ('industry' in patch || 'industryOther' in patch) {
          setVariables(current => ({ ...current, target_audience: audienceFor(next) }));
        }
        return next;
      }),
    [],
  );

  // Arriving from the Templates page with an industry chosen there (?industry=...): start with it picked, which also
  // writes the target-audience sentence exactly as picking it by hand would.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('industry');
    if (wanted && wanted !== OTHER_INDUSTRY && (INDUSTRIES as readonly string[]).includes(wanted)) setIcpField({ industry: wanted });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The profile has moved since the copy was written.
   *
   * Reported, never acted on. Rewriting automatically would take back sentences
   * the user has already reviewed and edited, which is the one thing this page
   * promises not to do - so the note points at the rewrite button instead.
   */
  const icpStale = useMemo(() => {
    if (!icpAtLastWrite) return false;
    const written = Object.values(salesContent).some(field => field.value.trim());
    return written && JSON.stringify(icpForRequest(icp)) !== icpAtLastWrite;
  }, [icp, icpAtLastWrite, salesContent]);

  /**
   * The audience moved after copy was written: rewrite what the AI wrote, leave what the person wrote.
   *
   * This used to only point at a button, so changing "SaaS" to "Healthcare" left the instructions and the template
   * values describing the old buyer and the flow came out as a generic sales script. Anything with origin 'edited'
   * (typed by the person, or a flow reopened from a saved one) is never touched; see audienceRefreshPlan.
   * Waits for the person to stop changing the profile, and runs once per profile.
   */
  const refreshedFor = useRef("");
  const icpKey = JSON.stringify(icpForRequest(icp));
  useEffect(() => {
    if (!icpAtLastWrite || icpKey === icpAtLastWrite || icpKey === refreshedFor.current || busyFields.length > 0) return;
    const material = (sourceText || describeManually(variables)).trim();
    if (material.length < MIN_SOURCE_CHARS) return;
    const plan = audienceRefreshPlan({
      salesContent,
      variableKeys: extraVariables.map(v => v.key),
      variables,
      variableOrigins,
      instructions,
    });
    if (!planHasWork(plan)) return;
    const timer = setTimeout(async () => {
      refreshedFor.current = icpKey;
      toast({ title: "Updating for the new audience", description: "Rewriting the AI-written parts. Anything you edited stays as you wrote it." });
      if (plan.clearValueProposition) setVariables(previous => ({ ...previous, value_proposition: "" }));
      if (plan.fields.length > 0 || plan.variableKeys.length > 0) await generateSales(plan.fields, material, false, "", plan.variableKeys);
      if (plan.instructions) await suggestInstructions(material);
    }, 1500);
    return () => clearTimeout(timer);
  }, [icpKey, icpAtLastWrite, busyFields, sourceText, variables, variableOrigins, salesContent, instructions, extraVariables, generateSales, suggestInstructions, toast]);

  /**
   * Fill Step 3 from everything the page knows.
   *
   * The one entry point for both the button in Step 2 and the automatic fill
   * below, so "what gets sent" is defined once.
   */
  const generateAll = useCallback(
    (force = false) => generateSales(
      SALES_FIELDS,
      sourceText || describeManually(variables),
      force,
      '',
      // The template's placeholders are written in the same pass. They are
      // fragments of the very sentences being written above them, and leaving
      // them out is what left five required boxes empty under a finished pitch.
      extraVariables.map(v => v.key),
    ),
    [extraVariables, generateSales, sourceText, variables],
  );

  /**
   * The single action behind the Generate button in Step 2.
   *
   * With a website address this is the whole pipeline - read the site, fill the
   * fields from it, write Step 3, draft the instructions. Without one it writes
   * Step 3 from whatever has been typed. Two separate buttons taught people to
   * read the site and then wonder why Step 3 was still empty.
   *
   * It forces a replace, because pressing it IS the request to replace what is
   * there. Nothing else on the page may take back reviewed copy.
   */
  const generateEverything = useCallback(() => {
    if (websiteUrl.trim()) return extractFromWebsite();
    return generateAll(true);
  }, [extractFromWebsite, generateAll, websiteUrl]);

  /** The line the agent will speak for {{target_audience}}, shown back to the user. */
  const audienceLine = useMemo(() => audienceFor(icp), [icp]);

  // -------------------------------------------------------------------- save

  /** Non-empty values for the template's own fields, under the keys it declared. */
  const variablesToSend = useMemo(() => {
    const payload: Record<string, string> = {};
    for (const variable of declaredVariables) {
      const entry = variable.key === 'value_proposition' && !variables.value_proposition
        ? salesContent.valueProposition.value
        : variables[variable.key];
      const text = String(entry ?? '').trim();
      if (text) payload[variable.key] = text;
    }
    return payload;
  }, [declaredVariables, salesContent.valueProposition.value, variables]);

  const markSubmitted = useCallback(() => setSubmitAttempted(true), []);

  /** Fill the page with the answers a flow was saved with, all of it counted as the user's own words. */
  const hydrate = useCallback((saved: SavedCustomization) => {
    setFlowName(saved.name);
    setLanguage(saved.language || "en");
    const saleText = (text?: string) => ({ value: text ?? "", origin: (text ? "edited" : "empty") as ContentOrigin });
    const values = saved.flowVariables ?? {};
    setVariables(values);
    setVariableOrigins(Object.fromEntries(Object.keys(values).map(key => [key, "edited" as ContentOrigin])));
    setSalesContent({
      valueProposition: saleText(saved.salesContent?.valueProposition),
      keyFeatures: saleText(saved.salesContent?.keyFeatures),
      objections: saleText(saved.salesContent?.objections),
    });
    setInstructions(saleText(saved.customInstructions));
    setSourceText(saved.sourceDocument ?? "");
    const profile = saved.icp ?? {};
    const industry = profile.industry ?? "";
    const known = INDUSTRIES.includes(industry as any);
    setIcp({
      ...emptyIcp(),
      industry: industry ? (known ? industry : OTHER_INDUSTRY) : "",
      industryOther: industry && !known ? industry : "",
      companySize: profile.companySize ?? "",
      region: profile.region ?? "",
      callScope: (["inbound", "outbound", "both"].includes(profile.callScope) ? profile.callScope : "") as IcpProfile["callScope"],
    });
  }, []);

  return {
    // step 1
    flowName, setFlowName,
    language, setLanguage,
    // step 2
    websiteUrl, setWebsiteUrl,
    variables, setVariable, value, raw, touch, variableOrigins, rewriteVariable,
    sourceText, setSourceText,
    extraction, extractFromWebsite, chooseProduct, resetExtraction,
    icp, setIcpField, icpStale, audienceLine, hasAudience: hasIcp(icp), generateAll, generateEverything,
    usingWebsite: websiteUrl.trim().length > 0,
    analysisSteps: ANALYSIS_STEPS,
    // step 3
    salesContent, setSalesField, rewrite, generateSales, busyFields,
    extraVariables, declares,
    // step 4
    instructions, setInstructions, suggestInstructions,
    // validation + save
    errors, showError, showAudienceError, isValid, markSubmitted, submitAttempted,
    variablesToSend, hydrate,
    hasSourceMaterial: sourceText.trim().length >= MIN_SOURCE_CHARS,
  };
}
